/**
 * RouteControls
 *
 * The five route pickers — WR1-WR4 and the RB — as one labeled group.
 *
 * ## Why this exists
 *
 * Picking routes is the primary verb of this app, and until now it had no control at all. The only
 * way to change a route was to HOVER a player glyph on the field, which meant:
 *
 *   - nothing on screen said routes were changeable (a `cursor: pointer` was the entire hint);
 *   - hover does not exist on a tablet, which is what a coach actually holds;
 *   - the glyph carried `role="button"` with no `tabIndex`, so it was keyboard-unreachable.
 *
 * `RouteSelector` and `RBRouteSelector` had been written for this and then left unrendered — the
 * barrel exported them but no page imported them. This component puts them back on screen.
 *
 * The on-field picker is kept as a shortcut for people who find it, but it is no longer the only
 * route in. See AnimationEngine, where it was also switched from hover to click.
 *
 * ## Why native <select>
 *
 * These are real `<select>` elements, unlike the app's custom div-based comboboxes
 * (CoverageSelector and friends). That is deliberate: a native select is keyboard- and
 * screen-reader-correct for free, works with mobile's native picker UI, and needs no
 * `aria-activedescendant` plumbing. For a plain one-of-N list there is no reason to rebuild it.
 *
 * ## Ids
 *
 * SimulationPage renders its whole control set twice (mobile and desktop layouts), so every id
 * here is namespaced by `idPrefix`. Without that the two copies collide and every `<label
 * htmlFor>` resolves to whichever select happens to come first in the document.
 */

import { RouteType, RBRouteType } from '../types';
import RouteSelector from './RouteSelector';
import RBRouteSelector from './RBRouteSelector';
import './RouteControls.css';

export interface RouteControlsProps {
  selectedRoute: RouteType;
  selectedRoute2: RouteType;
  selectedRoute3: RouteType;
  selectedRoute4: RouteType;
  selectedRBRoute: RBRouteType;
  onRouteChange: (route: RouteType) => void;
  onRoute2Change: (route: RouteType) => void;
  onRoute3Change: (route: RouteType) => void;
  onRoute4Change: (route: RouteType) => void;
  onRBRouteChange: (route: RBRouteType) => void;
  disabled?: boolean;
  /** Namespace for the generated ids — must differ between the mobile and desktop instances. */
  idPrefix: string;
}

/**
 * Receiver labels carry the coaching letter alongside the internal number.
 *
 * A coach thinks in X / H / Y / Z, not WR1-WR4, but the field glyphs are labelled WR1-WR4 — so
 * showing both is what lets someone map the control to the player they are looking at. The letters
 * match the hover tooltips in utils/positionDescriptions.
 */
const RECEIVERS = [
  { key: 'wr1', label: 'WR1 (X)', variant: 'wr1' as const },
  { key: 'wr2', label: 'WR2 (H)', variant: 'wr2' as const },
  { key: 'wr3', label: 'WR3 (Y)', variant: 'wr3' as const },
  { key: 'wr4', label: 'WR4 (Z)', variant: 'wr4' as const },
];

function RouteControls({
  selectedRoute,
  selectedRoute2,
  selectedRoute3,
  selectedRoute4,
  selectedRBRoute,
  onRouteChange,
  onRoute2Change,
  onRoute3Change,
  onRoute4Change,
  onRBRouteChange,
  disabled = false,
  idPrefix,
}: RouteControlsProps) {
  const routes = [selectedRoute, selectedRoute2, selectedRoute3, selectedRoute4];
  const handlers = [onRouteChange, onRoute2Change, onRoute3Change, onRoute4Change];

  return (
    <div className="route-controls">
      {RECEIVERS.map((receiver, index) => {
        const id = `${idPrefix}-${receiver.key}-route`;
        return (
          <div className="route-control-row" key={receiver.key}>
            <label className="route-control-label" htmlFor={id}>
              {receiver.label}
            </label>
            <RouteSelector
              id={id}
              selectedRoute={routes[index]}
              onRouteChange={handlers[index]}
              disabled={disabled}
              variant={receiver.variant}
            />
          </div>
        );
      })}

      <div className="route-control-row">
        <label className="route-control-label" htmlFor={`${idPrefix}-rb-route`}>
          RB
        </label>
        <RBRouteSelector
          id={`${idPrefix}-rb-route`}
          selectedRoute={selectedRBRoute}
          onRouteChange={onRBRouteChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default RouteControls;
